/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The 商机 (lead) list. An always-visible status row (scrollable chips + a
 * filter toggle) sits above a collapsible filter panel (search, date range,
 * sort/owner), then the KPI grid, then an infinite list of lead cards. The
 * panel is hidden by default so the list gets the room. Every control feeds the
 * shared `filter` / `keyword` / `sorter` driving both the list and the stats.
 */

import { useAIAssistantForPage } from '@euler/app/components/ai/pageContext';
import { RefreshHeaderButton } from '@euler/app/components/list/RefreshHeaderButton';
import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { FooterView } from '@euler/app/flows/home/components/FooterView';
import { LeadsNavParams } from '@euler/app/flows/leads';
import { useLeadCenter } from '@euler/app/flows/leads/LeadCenterContext';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { CreateLeadHeaderButton } from '@euler/app/flows/leads/pages/list/components/CreateLeadHeaderButton';
import { LeadDrawerHeaderButton } from '@euler/app/flows/leads/pages/list/components/LeadDrawerHeaderButton';
import { LeadListEndHint } from '@euler/app/flows/leads/pages/list/components/LeadListEndHint';
import { LeadFilterPanel } from '@euler/app/flows/leads/pages/list/components/filter-panel';
import { LeadKpiGrid } from '@euler/app/flows/leads/pages/list/components/kpi';
import { LeadCell } from '@euler/app/flows/leads/pages/list/components/lead-card';
import { StatusFilterRow } from '@euler/app/flows/leads/pages/list/components/status-filter';
import { useLeadListControls } from '@euler/app/flows/leads/pages/list/functions/useLeadListControls';
import { useLeadMonthlyNewCount } from '@euler/app/flows/leads/pages/list/functions/useLeadMonthlyNewCount';
import { useLeadStats } from '@euler/app/flows/leads/pages/list/functions/useLeadStats';
import { useLeadsList } from '@euler/app/flows/leads/pages/list/functions/useLeadsList';
import { Center } from '@euler/components/Center';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { Lead } from '@euler/model/lead/Lead';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useState } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  View,
} from 'react-native';

const keyExtractor = (item: Lead) => item.id;

export const LeadsListScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const contentContainerStyle = useScreenContentContainerStyle();
    const navigation = useNavigation<StackNavigationProp<LeadsNavParams>>();

    useAIAssistantForPage('leads_center');

    const controls = useLeadListControls();
    const { filter, sorter } = controls;

    // The filter panel is collapsed by default so the list gets the room; the
    // status row stays visible and its toggle reveals the panel.
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const toggleFilters = useCallback(
      () => setFiltersExpanded(prev => !prev),
      [],
    );

    const {
      data,
      status,
      replace,
      refresh,
      fetchMore,
      fetchSinceLastError,
      update,
    } = useLeadsList(filter, { sorter });
    // The KPI tiles are separate queries from the list, so a reload has to bump
    // them too — otherwise pulling (or tapping refresh) leaves stale counts over
    // a freshly-loaded list.
    const [statsReloadKey, setStatsReloadKey] = useState(0);
    const stats = useLeadStats(filter, statsReloadKey);
    const monthlyNewCount = useLeadMonthlyNewCount(statsReloadKey);

    const reloadAll = useCallback(() => {
      void refresh();
      setStatsReloadKey(k => k + 1);
    }, [refresh]);

    // The burger (opens the app drawer) sits on the left; the "+" on the right
    // opens the manual create-lead form. On web a reload button joins it — the
    // browser has no pull-to-refresh.
    useEffect(() => {
      navigation.setOptions({
        headerLeft: () => <LeadDrawerHeaderButton />,
        headerRight: () => (
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {Platform.OS === 'web' ? (
              <RefreshHeaderButton
                onPress={reloadAll}
                refreshing={status.isRefreshing ?? false}
              />
            ) : null}
            <CreateLeadHeaderButton />
          </View>
        ),
      });
    }, [navigation, reloadAll, status.isRefreshing]);

    // Patch a single card in place when any screen in the flow reports a lead
    // change (detail edit, transition, reservation…), so it reflects on return
    // without a full reload or losing scroll position. Decoupled from nav params
    // via the lead-center subject, so deep-linking is unaffected.
    const { leadUpdated$ } = useLeadCenter();
    useEffect(() => {
      const sub = leadUpdated$.subscribe(lead => {
        update(item => {
          if (item.id === lead.id) Object.assign(item, lead);
          return undefined;
        });
      });
      return () => sub.unsubscribe();
    }, [leadUpdated$, update]);

    const onPress = useCallback(
      (lead: Lead) => {
        navigation.navigate('detail', { id: lead.id });
      },
      [navigation],
    );

    const renderItem: ListRenderItem<Lead> = useCallback(
      ({ item }) => <LeadCell lead={item} onPress={onPress} />,
      [onPress],
    );

    // Reload from offset 0 whenever any control changes the combined inputs.
    // `replace` resets the data and stamps a requestId, so an in-flight page
    // load (or a slower earlier reload) can't clobber the newer result set.
    useEffect(() => {
      void replace();
      // `replace` is a stable persisted fn; only the query inputs should
      // retrigger the reload. The keyword is part of `filter` now.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, sorter]);

    const header = (
      <View style={{ paddingTop: rpx(12) }}>
        <StatusFilterRow
          value={controls.statusKey}
          onChange={controls.setStatusKey}
          filtersExpanded={filtersExpanded}
          onToggleFilters={toggleFilters}
        />
        {filtersExpanded ? <LeadFilterPanel controls={controls} /> : null}
        <LeadKpiGrid stats={stats} monthlyNewCount={monthlyNewCount} />
      </View>
    );

    const showEndHint =
      !status.hasMore && data.length > 0 && !status.isLoading && !status.error;

    return (
      <View style={{ flex: 1, backgroundColor: LD.canvas }}>
        <FlatList
          data={data}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          ListHeaderComponent={header}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          contentContainerStyle={[{ flexGrow: 1 }, contentContainerStyle]}
          refreshControl={
            Platform.OS === 'web' ? undefined : (
              <RefreshControl
                refreshing={(status.isRefreshing && data.length > 0) ?? false}
                onRefresh={reloadAll}
              />
            )
          }
          onEndReachedThreshold={0.3}
          onEndReached={fetchMore}
          ListEmptyComponent={
            status.isLoading && data.length === 0 && !status.isRefreshing ? (
              <Center css={'flex: 1;'}>
                <DefaultLoader />
              </Center>
            ) : (
              <EmptyView
                error={status.error}
                onRetry={reloadAll}
                message={'暂无商机'}
              />
            )
          }
          ListFooterComponent={
            showEndHint ? (
              <LeadListEndHint text="没有更多商机了" />
            ) : (
              <FooterView
                isLoading={
                  status.isLoading &&
                  status.loadedDataLength > 0 &&
                  !status.isRefreshing
                }
                total={data.length}
                error={status.error}
                hasMore={status.hasMore}
                noMoreText="没有更多商机了"
                onRetry={fetchSinceLastError}
              />
            )
          }
        />
      </View>
    );
  },
  {
    title: '商机',
  },
);
